import { AnimatePresence, MotionConfig, fadeUp, motion, stagger } from '@/lib/motion';
import { MARKETING_PLAN_SPECS, marketingQuotaLine } from '@platform/types';
import { ArrowRight, CheckCircle2, ChevronDown, Smartphone } from 'lucide-react';
import { Suspense, lazy, useState } from 'react';
import { Link } from 'react-router-dom';

import { useBrand } from '@/brand/brand-provider';
import { GuaranteeBadge, useMoneyBackAvailable } from '@/components/marketing/guarantee-badge';
import { DualChannelHero } from '@/components/marketing/dual-channel-hero';
import { StatsBand } from '@/components/marketing/stats-band';
import { StickyMobileCta } from '@/components/marketing/sticky-mobile-cta';
import { Button } from '@/components/ui/button';
import { iosUrl, androidUrl } from '@/lib/app-links';
import { track } from '@/lib/analytics';
import { JsonLd, Seo } from '@/lib/seo';

// Below-fold sections load as one deferred chunk (see landing-sections.ts) so
// the entry chunk only carries what the first viewport needs.
const sections = () => import('@/components/marketing/landing-sections');
const ChannelCards = lazy(() => sections().then((m) => ({ default: m.ChannelCards })));
const DemoSection = lazy(() => sections().then((m) => ({ default: m.DemoSection })));
const IndustriesSection = lazy(() => sections().then((m) => ({ default: m.IndustriesSection })));
const KnowledgeCoreDiagram = lazy(() => sections().then((m) => ({ default: m.KnowledgeCoreDiagram })));
const MissedCallPain = lazy(() => sections().then((m) => ({ default: m.MissedCallPain })));
const MobileAppSection = lazy(() => sections().then((m) => ({ default: m.MobileAppSection })));
const SavingsCalculator = lazy(() => sections().then((m) => ({ default: m.SavingsCalculator })));
const Testimonials = lazy(() => sections().then((m) => ({ default: m.Testimonials })));

/** Height-reserving Suspense wrapper — below-fold sections pop in without
 *  collapsing the scroll position while their chunk loads. */
function Deferred({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <Suspense fallback={<div aria-hidden className="min-h-[320px]" />}>
      {children}
    </Suspense>
  );
}

export function LandingPage(): JSX.Element {
  const brand = useBrand();
  return (
    <MotionConfig reducedMotion="user">
      <Seo
        title={`${brand.displayName} — answer every call and every chat from one knowledge base`}
        description="Your AI receptionist works the phones and your website — same answers, same brand, one dashboard. Voice agent + embeddable chat widget on one knowledge base. Live in minutes."
        path="/"
      />
      <JsonLd
        schema={{
          '@context': 'https://schema.org',
          '@graph': [
            {
              '@type': 'Organization',
              '@id': `${window.location.origin}/#organization`,
              name: brand.displayName,
              url: window.location.origin,
              logo: brand.logoUrl
                ? { '@type': 'ImageObject', url: brand.logoUrl }
                : undefined,
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'customer support',
                email: brand.supportEmail,
                telephone: '+1-315-809-3245',
              },
              address: {
                '@type': 'PostalAddress',
                addressLocality: 'Chicago',
                addressRegion: 'IL',
                addressCountry: 'US',
              },
            },
            {
              '@type': 'WebSite',
              '@id': `${window.location.origin}/#website`,
              url: window.location.origin,
              name: brand.displayName,
              publisher: { '@id': `${window.location.origin}/#organization` },
            },
            {
              '@type': 'SoftwareApplication',
              name: brand.displayName,
              applicationCategory: 'BusinessApplication',
              operatingSystem: 'Web',
              description:
                'AI receptionist platform for small businesses. Answers phone calls and website chat 24/7 from one knowledge base.',
              offers: {
                '@type': 'Offer',
                url: `${window.location.origin}/pricing`,
              },
              publisher: { '@id': `${window.location.origin}/#organization` },
            },
            {
              '@type': 'FAQPage',
              mainEntity: faqs.filter((f) => !('guarantee' in f)).map((f) => ({
                '@type': 'Question',
                name: f.q,
                acceptedAnswer: { '@type': 'Answer', text: f.a },
              })),
            },
          ],
        }}
      />
      {/* Conversion-ordered: proof → demo → how → what → who → cost → price.
          Deep product detail (agent roles, setup walkthrough, done-for-you,
          add-ons) lives on /voice and /pricing now. */}
      <Hero brandName={brand.displayName} />
      <StatsBand />
      <Deferred><DemoSection /></Deferred>
      <Deferred><MobileAppSection /></Deferred>
      <HowItWorks />
      <Deferred><ChannelCards /></Deferred>
      <Deferred><KnowledgeCoreDiagram /></Deferred>
      <Showcase brandName={brand.displayName} />
      <Deferred><IndustriesSection /></Deferred>
      <Deferred><MissedCallPain /></Deferred>
      <Deferred><SavingsCalculator /></Deferred>
      <Deferred><Testimonials /></Deferred>
      <PricingTeaser />
      <FAQ />
      <CTA brandName={brand.displayName} />
      <StickyMobileCta />
    </MotionConfig>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hero
// ─────────────────────────────────────────────────────────────────────────────

const HERO_INDUSTRIES = [
  { to: '/industries/dental', label: 'Dental' },
  { to: '/industries/legal', label: 'Law firms' },
  { to: '/industries/trades', label: 'Trades' },
  { to: '/industries/real-estate', label: 'Real estate' },
  { to: '/industries/medical', label: 'Medical' },
  { to: '/industries/salons', label: 'Salons' },
] as const;

function Hero({ brandName }: { brandName: string }): JSX.Element {
  return (
    <section className="relative overflow-hidden">
      {/* Soft gradient backdrop with slow-drifting glow orbs. */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-0 h-[600px] w-[1000px] -translate-x-1/2 -translate-y-1/3 rounded-full bg-gradient-to-br from-[var(--brand-accent)]/20 via-[var(--brand-accent)]/5 to-transparent blur-3xl" />
        <div className="animate-orb-drift-a absolute right-0 top-32 h-72 w-72 rounded-full bg-emerald-500/10 blur-3xl" />
        <div className="animate-orb-drift-b absolute bottom-0 left-0 h-72 w-72 rounded-full bg-fuchsia-500/10 blur-3xl" />
      </div>

      <div className="container py-14 sm:py-20 lg:py-28">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
          className="mx-auto max-w-3xl text-center"
        >
          <motion.div variants={fadeUp}>
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background/60 px-4 py-1.5 text-xs font-medium text-muted-foreground backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              One knowledge base · Phone + website · Sub-second response
            </span>
          </motion.div>
          <motion.div variants={fadeUp} className="mt-3">
            <a
              href="#mobile-app"
              className="inline-flex items-center gap-2 rounded-full bg-[var(--brand-accent)]/10 px-4 py-1.5 text-xs font-medium text-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/20"
            >
              <Smartphone className="h-3.5 w-3.5" />
              {iosUrl && androidUrl ? 'Get the iPhone & Android app' : 'iPhone & Android apps coming soon'}
              <ArrowRight className="h-3 w-3" />
            </a>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="mt-6 text-4xl font-bold tracking-tight sm:text-6xl lg:text-7xl"
          >
            Answer every call{' '}
            <span className="bg-gradient-to-r from-[var(--brand-accent)] to-fuchsia-500 bg-clip-text text-transparent">
              and every chat.
            </span>
            <br />
            From one knowledge base.
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="mx-auto mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg"
          >
            {brandName} works the phones and your website — same answers, same
            brand, one dashboard. Train it once; it picks up every call and every
            website visitor. Live in minutes.
          </motion.p>

          <motion.div
            variants={fadeUp}
            className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row"
          >
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/signup" onClick={() => track('hero_cta_click')}>
                Get started
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="w-full sm:w-auto">
              <a href="#demo" onClick={() => track('hero_demo_click')}>
                See it in action
              </a>
            </Button>
          </motion.div>

          <motion.p variants={fadeUp} className="mt-4 text-xs text-muted-foreground">
            From $39/month · Voice + chat included · Cancel anytime
          </motion.p>
          <motion.div variants={fadeUp} className="mt-3 flex justify-center">
            <GuaranteeBadge />
          </motion.div>

          {/* Trust row: real industries we ship pages for — no invented logos
              or review counts. Doubles as internal links for SEO. */}
          <motion.div
            variants={fadeUp}
            className="mt-8 flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-xs text-muted-foreground"
          >
            <span className="font-medium uppercase tracking-wide">Built for</span>
            {HERO_INDUSTRIES.map((i) => (
              <Link
                key={i.to}
                to={i.to}
                className="rounded-full border border-border bg-background/60 px-3 py-1 backdrop-blur transition-colors hover:border-[var(--brand-accent)] hover:text-foreground"
              >
                {i.label}
              </Link>
            ))}
          </motion.div>
        </motion.div>

        {/* Hero dual-channel split: live call + live chat, one shared brain */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="mt-16"
        >
          <DualChannelHero />
        </motion.div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// How it works
// ─────────────────────────────────────────────────────────────────────────────

const steps = [
  {
    title: 'Pick a number, paste a snippet',
    desc: 'Provision a local number for the phone agent and drop one script tag on your site for chat — both in a few clicks.',
  },
  {
    title: 'Train your agent',
    desc: 'Upload a greeting, your hours, FAQs, and documents once. The same knowledge base powers both channels. No code.',
  },
  {
    title: 'Go live on both channels',
    desc: 'Watch real-time transcripts from calls and chats, review summaries, tweak prompts, iterate — without re-training.',
  },
];

function HowItWorks(): JSX.Element {
  return (
    <section
      id="how-it-works"
      className="scroll-mt-24 border-y border-border bg-muted/20 py-14 sm:py-20"
    >
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">Live in minutes.</h2>
          <p className="mt-4 text-muted-foreground">No engineering team required. No code.</p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-8 md:grid-cols-3">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.15 }}
              className="relative"
            >
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--brand-accent)] text-lg font-bold text-white shadow-lg shadow-[var(--brand-accent)]/30">
                {i + 1}
              </div>
              <h3 className="mt-5 text-lg font-semibold">{s.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              {i < steps.length - 1 ? (
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + i * 0.15, duration: 0.5 }}
                  style={{ originX: 0 }}
                  className="absolute right-0 top-6 hidden h-px w-full -translate-x-12 bg-gradient-to-r from-[var(--brand-accent)]/40 to-transparent md:block"
                />
              ) : null}
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-center text-sm text-muted-foreground">
          Average setup: from signup to your first answered call or chat in under 10 minutes.
        </p>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Showcase
// ─────────────────────────────────────────────────────────────────────────────

function Showcase({ brandName }: { brandName: string }): JSX.Element {
  return (
    <section className="container py-14 sm:py-20">
      <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">
        <div>
          <span className="text-xs font-semibold uppercase tracking-widest text-[var(--brand-accent)]">
            Built for control
          </span>
          <h2 className="mt-3 text-3xl font-bold tracking-tight sm:text-4xl">
            One dashboard. Total visibility.
          </h2>
          <p className="mt-4 text-muted-foreground">
            Every call and chat transcribed, summarized, and searchable. Spot trends,
            fix prompts, train smarter — all from {brandName}&apos;s dashboard.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              'Real-time call and chat activity',
              'AI-generated summaries & outcomes',
              'Searchable transcripts across both channels',
              'Per-agent, per-number, and per-widget analytics',
              'Webhook export to your own tools',
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm">
                <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-500" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8">
            <Button asChild size="lg">
              <Link to="/signup">
                Get started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        <div className="relative">
          <div className="absolute -inset-4 -z-10 rounded-3xl bg-gradient-to-tr from-[var(--brand-accent)]/20 to-emerald-500/10 blur-2xl" />
          <DashboardPreview />
        </div>
      </div>
    </section>
  );
}

function DashboardPreview(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
      <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
          <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/30" />
        </div>
        <span className="text-xs text-muted-foreground">app · dashboard</span>
        <span className="w-12" />
      </div>
      <div className="grid grid-cols-2 gap-3 p-4">
        <MiniStat label="Calls today" value="34" trend="+12%" />
        <MiniStat label="Chats today" value="58" trend="+21%" />
        <MiniStat label="Requests captured" value="19" trend="+4" />
        <MiniStat label="Transferred" value="3" trend="+1" />
      </div>
      <div className="border-t border-border p-4">
        <p className="mb-3 text-xs font-medium text-muted-foreground">Conversations this week</p>
        <MiniBars />
      </div>
    </div>
  );
}

function MiniStat({
  label,
  value,
  trend,
}: {
  label: string;
  value: string;
  trend: string;
}): JSX.Element {
  const up = trend.startsWith('+');
  return (
    <div className="rounded-lg bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-xl font-semibold">{value}</p>
      <p className={`mt-0.5 text-xs ${up ? 'text-emerald-500' : 'text-amber-500'}`}>{trend}</p>
    </div>
  );
}

function MiniBars(): JSX.Element {
  const bars = [40, 65, 50, 75, 60, 85, 55];
  return (
    <div className="flex items-end justify-between gap-2">
      {bars.map((h, i) => (
        <div key={i} className="flex flex-1 flex-col items-center gap-1">
          <motion.div
            initial={{ scaleY: 0 }}
            whileInView={{ scaleY: 1 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.06, duration: 0.4 }}
            style={{ height: `${h}px`, originY: 1 }}
            className="w-full rounded-t bg-gradient-to-t from-[var(--brand-accent)] to-fuchsia-500"
          />
          <span className="text-[10px] text-muted-foreground">
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'][i]}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pricing teaser
// ─────────────────────────────────────────────────────────────────────────────

function PricingTeaser(): JSX.Element {
  return (
    <section className="container py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          One bill. Both channels.
        </h2>
        <p className="mt-4 text-muted-foreground">
          Every plan bundles voice and chat on one knowledge base — a flat monthly
          price with inbound voice minutes and chat messages included.
        </p>
      </div>

      <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-6 sm:grid-cols-3">
        {MARKETING_PLAN_SPECS.map((spec) => (
          <TeaserCard
            key={spec.slug}
            name={spec.name}
            price={`$${spec.priceCents / 100}`}
            sub={marketingQuotaLine(spec)}
            highlighted={spec.highlighted}
          />
        ))}
      </div>

      <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Button asChild size="lg">
          <Link to="/pricing">
            See full pricing <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
        </Button>
        <Button asChild variant="ghost" size="lg">
          <Link to="/contact">Talk to us</Link>
        </Button>
      </div>
      <div className="container mt-8">
        <GuaranteeBadge variant="banner" />
      </div>
    </section>
  );
}

function TeaserCard({
  name,
  price,
  sub,
  highlighted = false,
}: {
  name: string;
  price: string;
  sub: string;
  highlighted?: boolean;
}): JSX.Element {
  return (
    <div
      className={`rounded-2xl border p-6 transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-0.5 motion-reduce:transition-none motion-reduce:hover:translate-y-0 ${
        highlighted
          ? 'border-[var(--brand-accent)] bg-[var(--brand-accent)]/5 shadow-lg shadow-[var(--brand-accent)]/10'
          : 'border-border bg-card hover:border-[var(--brand-accent)]/40 hover:shadow-md'
      }`}
    >
      {highlighted ? (
        <span className="mb-3 inline-block rounded-full bg-[var(--brand-accent)] px-2.5 py-0.5 text-xs font-medium text-white">
          Most popular
        </span>
      ) : null}
      <h3 className="font-semibold">{name}</h3>
      <p className="mt-3 text-3xl font-bold">
        {price}
        <span className="text-base font-normal text-muted-foreground">/mo</span>
      </p>
      <p className="mt-1 text-sm text-muted-foreground">{sub}</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <span className="rounded-full bg-[var(--brand-accent)]/10 px-2.5 py-0.5 text-xs font-medium text-[var(--brand-accent)]">
          Voice
        </span>
        <span className="rounded-full bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          Chat
        </span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FAQ
// ─────────────────────────────────────────────────────────────────────────────

const cheapestPlan = MARKETING_PLAN_SPECS.reduce((min, p) =>
  p.priceCents < min.priceCents ? p : min,
);

const faqs = [
  {
    q: 'How do I find out about calls and new leads?',
    a: 'Instantly, three ways. Our iOS and Android app sends a push notification after every call with the caller, outcome and summary. You also get an email for every call and every new website-chat lead, and every call, transcript and lead is in your dashboard (leads export to CSV). Urgent calls can be transferred straight to your mobile on Growth and Scale.',
  },
  {
    q: 'Do I need a new phone number?',
    a: 'No. Keep your existing number and forward calls to your AI receptionist — all the time, only after hours, or only when you don’t pick up. We give you a local number to forward to, and you can switch forwarding off any time.',
  },
  {
    q: 'What happens with an emergency or a caller who wants a person?',
    a: 'You write transfer rules in plain English, like “gas leak or flooding: transfer to my mobile”. The agent announces the transfer and connects the caller. If you can’t take it, it captures the details and alerts you right away.',
  },
  {
    q: 'Can it book appointments into my calendar?',
    a: 'Yes. It checks real availability in Google Calendar, Microsoft 365 or Calendly and books the slot while the caller is on the line, so it never double-books you. Leads can also go to HubSpot, Slack, Teams, Zapier or a webhook.',
  },
  {
    q: 'Will my customers know it’s an AI?',
    a: 'It sounds natural and answers in under a second, and it says honestly that it’s an AI assistant if asked. Most callers simply care that someone picked up, answered their question and booked them in.',
  },
  {
    q: 'What does it know about my business?',
    a: 'Whatever you give it: your services, prices, hours, service area and FAQs. Import your website or upload documents and it answers from that, the same on the phone and in your website chat. If it doesn’t know, it says so and takes a message instead of guessing.',
  },
  {
    q: 'How long does setup take?',
    a: 'About 15 minutes yourself: pick a plan, import your website, set your hours and forward your number. Or choose done-for-you setup and our team builds it, tests it and takes it live within 72 hours.',
  },
  {
    q: 'How does pricing work? Is there a contract?',
    a: `One flat monthly price covers voice and chat, with minutes and messages included. ${cheapestPlan.name} starts at $${cheapestPlan.priceCents / 100}/month. No contract, no setup fee for self-serve, cancel anytime. Extra usage is only charged if you turn it on, up to a cap you set.`,
  },
  {
    q: 'What if it doesn’t work for my business?',
    a: 'You’re covered by our 30-day money-back guarantee. If you’re not happy within 30 days of your first payment, email us and we refund your first month in full.',
    guarantee: true,
  },
  {
    q: 'Is my data private?',
    a: 'Yes. Each business’s data is isolated and encrypted in transit and at rest, and we never train shared AI models on your calls. You can delete your account and data anytime from settings.',
  },
];

function FAQ(): JSX.Element {
  const moneyBack = useMoneyBackAvailable();
  const items = faqs.filter((f) => moneyBack || !('guarantee' in f));
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section className="border-y border-border bg-muted/20 py-14 sm:py-20">
      <div className="container">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
            Frequently asked questions
          </h2>
        </div>
        <div className="mx-auto mt-10 max-w-2xl divide-y divide-border rounded-2xl border border-border bg-card">
          {items.map((f, i) => (
            <div key={f.q}>
              <button
                type="button"
                onClick={() => setOpen(open === i ? null : i)}
                className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left transition-colors hover:bg-muted/30"
                aria-expanded={open === i}
              >
                <span className="font-medium">{f.q}</span>
                <motion.span
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                  className="flex-shrink-0"
                >
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                </motion.span>
              </button>
              <AnimatePresence initial={false}>
                {open === i ? (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.25, ease: 'easeInOut' }}
                    className="overflow-hidden"
                  >
                    <div className="px-5 pb-4 text-sm text-muted-foreground">{f.a}</div>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// CTA
// ─────────────────────────────────────────────────────────────────────────────

function CTA({ brandName }: { brandName: string }): JSX.Element {
  const moneyBack = useMoneyBackAvailable();
  return (
    <section className="container py-14 sm:py-20">
      <div className="relative overflow-hidden rounded-3xl border border-border bg-gradient-to-br from-[var(--brand-accent)] to-fuchsia-600 px-6 py-16 text-center text-white sm:px-12">
        <div className="pointer-events-none absolute inset-0 opacity-20">
          <div className="animate-orb-pulse absolute -right-12 -top-12 h-48 w-48 rounded-full bg-white blur-3xl" />
          <div className="animate-orb-pulse-late absolute -bottom-12 -left-12 h-48 w-48 rounded-full bg-white blur-3xl" />
        </div>
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          Never miss a call or a chat again.
        </h2>
        <p className="mx-auto mt-4 max-w-xl text-white/80">
          Set up your first {brandName} agent in minutes. From $39/month with voice
          and chat included on one knowledge base.{moneyBack ? ' 30-day money-back guarantee.' : ''}
        </p>
        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button
            asChild
            size="lg"
            className="w-full bg-white text-[var(--brand-accent)] hover:bg-white/90 sm:w-auto"
          >
            <Link to="/signup">
              Get started
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="w-full border-white/40 bg-transparent text-white hover:bg-white/10 sm:w-auto"
          >
            <Link to="/pricing">See pricing</Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
